import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom-v5-compat';

export type ButtonV2Props = ButtonV2AnchorProps | ButtonV2ButtonProps | ButtonV2LinkProps;

export type ButtonV2AnchorProps = Omit<React.ComponentPropsWithoutRef<'a'>, OmittedProps> &
  AdditionalProps & { href: string };
export type ButtonV2LinkProps = Omit<LinkProps, OmittedProps> & AdditionalProps;
export type ButtonV2ButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, OmittedProps> & AdditionalProps;

type OmittedProps = 'prefix';

interface AdditionalProps {
  /**
   * Whether or not the button is active. Used for dropdown menu toggles when
   * the dropdown is open.
   */
  active?: boolean;

  /**
   * The button's label.
   */
  children?: React.ReactNode;

  /**
   * Whether or not the button is disabled. Works even if the button is a link.
   */
  disabled?: boolean;

  /**
   * Content (usually an icon, 16x16 ones work best) to replace the button's
   * label. Screen readers would skip and read the original label instead.
   */
  infix?: React.ReactNode;

  /**
   * Content (usually an icon, 16x16 ones work best) to place after the button's
   * label. Screen readers will skip it.
   */
  postfix?: React.ReactNode;

  /**
   * How to position the postfix relatively to the button's label.
   */
  postfixPosition?: AffixPosition;

  /**
   * Same as {@link AdditionalProps.postfix}, but before the button's label.
   */
  prefix?: React.ReactNode;

  /**
   * Same as {@link AdditionalProps.postfixPosition}, but for the prefix.
   */
  prefixPosition?: AffixPosition;

  variant?: Variant;
}

type AffixPosition = 'close' | 'away';
export type Variant = 'primary' | 'outline' | 'warn' | 'danger';

export const ButtonV2: {
  (props: ButtonV2AnchorProps & { ref?: React.Ref<HTMLAnchorElement> }): React.ReactElement | null;
  (props: ButtonV2ButtonProps & { ref?: React.Ref<HTMLButtonElement> }): React.ReactElement | null;
  (props: ButtonV2LinkProps & { ref?: React.Ref<HTMLAnchorElement> }): React.ReactElement | null;
  (props: ButtonV2Props & { ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement> }): React.ReactElement | null;
} = forwardRef(
  (
    {
      active = false,
      children,
      disabled = false,
      infix,
      postfix,
      postfixPosition = 'close',
      prefix,
      prefixPosition = 'close',
      variant = 'outline',
      className,
      ...polymorphicProps
    },
    ref,
  ) => {
    // The button wants to be narrower when it displays just the icons:
    const isTextless = !!infix;

    const content = (
      <>
        {prefix && <Prefix position={prefixPosition}>{prefix}</Prefix>}
        {infix ? <Infix label={children}>{infix}</Infix> : <span className="truncate">{children}</span>}
        {postfix && <Postfix position={postfixPosition}>{postfix}</Postfix>}
      </>
    );

    const classes = clsx(buttonClasses({ isActive: active, isDisabled: disabled, isTextless, variant }), className);

    if ('to' in polymorphicProps) {
      return (
        <Link
          aria-disabled={disabled}
          role="button"
          className={classes}
          {...polymorphicProps}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {content}
        </Link>
      );
    }

    if ('href' in polymorphicProps) {
      return (
        <a
          aria-disabled={disabled}
          role="button"
          className={classes}
          {...polymorphicProps}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        disabled={disabled}
        type="button"
        className={classes}
        {...polymorphicProps}
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
      >
        {content}
      </button>
    );
  },
);

const Postfix = ({ children, position }: { children?: React.ReactNode; position: AffixPosition }) => (
  <AffixContainer aria-hidden className={clsx('pl-1', position === 'away' && 'ml-auto')}>
    {children}
  </AffixContainer>
);

const Infix = ({ children, label }: { children?: React.ReactNode; label?: React.ReactNode } = {}) => (
  <>
    <AffixContainer aria-hidden>{children}</AffixContainer>
    {label && <span className="sr-only">{label}</span>}
  </>
);

const Prefix = ({ children, position }: { children?: React.ReactNode; position: AffixPosition }) => (
  <AffixContainer aria-hidden className={clsx('pr-1', position === 'away' && 'mr-auto')}>
    {children}
  </AffixContainer>
);

interface ButtonStyleArgs {
  isActive: boolean;
  isDisabled: boolean;
  isTextless: boolean;
  variant: Variant;
}

const buttonClasses = ({ isActive, isDisabled, isTextless, variant }: ButtonStyleArgs) =>
  clsx(
    'align-middle',
    'border',
    'inline-flex',
    'items-center',
    'justify-center',
    'min-h-[2.5rem]',
    'min-w-[2.5rem]',
    'rounded-lg',
    'text-sm/snug',
    'font-semibold',
    'whitespace-nowrap',
    'transition duration-fast',

    // Normalize the centering between <a> and <button> elements
    'text-center',

    // Prevent .sr-only elements from messing up scroll behavior
    // https://github.com/tailwindlabs/tailwindcss/issues/4330
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1154640
    'relative',

    'focus:outline-none focus-visible:ring-4',
    isActive && 'border border-secondary ring-4 ring-secondary-lighter',

    // The button will more eagerly try to stick to its square shape defined by
    // min-width and min-height:
    isTextless ? 'px-1' : 'px-2',

    // Disabled state base:
    isDisabled && 'pointer-events-none',

    // "Primary" variant:
    variant === 'primary' && [
      'border-transparent',
      'hover:bg-primary-dark',
      'active:bg-primary',
      'focus-visible:ring-4 ring-secondary-light',
      isDisabled ? 'bg-gray-50 text-gray-5' : 'bg-primary text-white',
    ],

    // "Outline" variant:
    variant === 'outline' && [
      'bg-white',
      'border-gray-15',
      'hover:text-primary',
      'active:bg-gray-5',
      'focus-visible:border-secondary',
      'focus-visible:ring-secondary-lighter',
      'focus-visible:text-primary',
      'focus-visible:hover:border-secondary',
      isActive && 'border-secondary',
      isDisabled ? 'text-gray-30' : 'text-primary-medium',
    ],

    // "Warn" variant:
    variant === 'warn' && [
      'bg-white',
      'border-error-light',
      'hover:border-error-dark',
      'active:bg-error-lightest',
      'focus-visible:border-error-dark',
      'focus-visible:ring-error-lightest',
      isDisabled ? 'text-error-light' : 'text-error-dark',
    ],

    // "Danger" variant:
    variant === 'danger' && [
      'bg-red',
      'border-transparent',
      'hover:bg-error-dark',
      'active:bg-error',
      'focus-visible:border-error-dark',
      'focus-visible:ring-error-lightest',
      isDisabled ? 'text-error-light' : 'text-white',
    ],
  );

const AffixContainer = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span className={clsx('flex shrink-0 items-center justify-center', className)} {...props} />
);
